import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import type { ArgoAttributeInsertInput, ValidationResult } from 'types';
import { ArgoAttributeDataConstraint, ArgoAttributeDataUpdateColumn, ArgoAttributeSchemaDefinitionConstraint, AttributeFragment, AttributeSchemaFragment, InsertSchemaDocument, UpdateSchemaDocument } from 'types/db';
import { validateAttributeData } from './attributes.validations';
import { gqlClient } from 'src/util/gql-client';
import { updateEntities } from './util';

export type AttributeSchemaPropertyFromFragment = (Pick<AttributeSchemaFragment['definitions'][number],
    'id' |
    'type' |
    'name' |
    'description' |
    'validationMax' |
    'validationMin' |
    'validationPattern' |
    'validationRequired'
> & {
    new: boolean;
});

export type EntityIds = {
    locationIds: string[];
    materialIds: string[];
    materialModelIds: string[];
    materialClassIds: string[];
}

export type EntitiesToUpdateOnSave = {
    oldAttributeId: string;
    newAttributeId: string;
} & EntityIds;

export type AttributeSchemaEntity = {
    entitiesToUpdateOnSave: EntitiesToUpdateOnSave[];
    schema: Pick<AttributeSchemaFragment,
        'id' |
        'type' |
        'meta' |
        'name' |
        'color' |
        'enabled' |
        'description'
    > & {
        definitions: AttributeSchemaPropertyFromFragment[];
        attributes: (Pick<AttributeSchemaFragment['attributes'][number],
            'id' |
            'name' |
            'meta' |
            'enabled' |
            'description' |
            'attribute_data'
        > & {
            new: boolean;
            created_at?: string;
            root_attribute_id: string;
            location_attributes_aggregate?: AttributeSchemaFragment['attributes'][number]['location_attributes_aggregate'];
            material_attributes_aggregate?: AttributeSchemaFragment['attributes'][number]['material_attributes_aggregate'];
            material_class_attributes_aggregate?: AttributeSchemaFragment['attributes'][number]['material_class_attributes_aggregate'];
            material_model_attributes_aggregate?: AttributeSchemaFragment['attributes'][number]['material_model_attributes_aggregate'];
        })[];
    };
};

export type AttributeForSchemaView = AttributeSchemaEntity['schema']['attributes'][number];

export type AttributeProperty = AttributeForSchemaView['attribute_data'][number];

export type AttributeDefinition = AttributeSchemaEntity['schema']['definitions'][number];

export type Attribute = Pick<AttributeFragment,
    'name'
    | 'attributeData'
    | 'attributeSchemaId'
    | 'id'
    | 'description'
    | 'enabled'
>;

export const createAttribute = async (payload: EntityModifyParameters<'attributeschema'>): Promise<ValidationResult> => {
    const { entity } = payload;
    const validationResult = validateAttributeData(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: InsertSchemaDocument,
            variables: {
                schemas: [
                    {
                        id: entity.schema.id,
                        type: entity.schema.type,
                        name: entity.schema.name,
                        meta: entity.schema.meta,
                        color: entity.schema.color,
                        enabled: entity.schema.enabled,
                        description: entity.schema.description,
                        attributes: {
                            data: entity.schema.attributes.map(attr => ({
                                id: attr.id,
                                meta: attr.name,
                                name: attr.name,
                                enabled: attr.enabled,
                                description: attr.description,
                                root_attribute_id: attr.root_attribute_id,
                                attribute_data: {
                                    data: attr.attribute_data.map(ad => ({
                                        id: ad.id,
                                        str_val: ad.str_val,
                                        num_val: ad.num_val,
                                        attribute_schema_definition_id: ad.attribute_schema_definition_id,
                                    })),
                                },
                            })),
                        },
                        attribute_schema_definitions: {
                            data: entity.schema.definitions.map(asd => ({
                                id: asd.id,
                                enabled: true,
                                name: asd.name,
                                type: asd.type,
                                meta: {},
                                validation_api_check: false,
                                description: asd.description,
                                validation_min: asd.validationMin,
                                validation_max: asd.validationMax,
                                validation_pattern: asd.validationPattern,
                                validation_required: asd.validationRequired,
                            })),
                            on_conflict: {
                                constraint: ArgoAttributeSchemaDefinitionConstraint.AttributeSchemaDefinitionPkey,
                                update_columns: [],
                            },
                        },
                    },
                ],
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Attribute: "${JSON.stringify(err)}"`,
        };
    }
};

export const saveAttribute = async (payload: ExtendedEntityParameters<'attributeschema'>): Promise<ValidationResult> => {
    const {
        entity,
        oldEntity: originalEntity,
    } = payload;
    const validationResult = validateAttributeData(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        await gqlClient.request({
            document: UpdateSchemaDocument,
            variables: {
                schemaId: entity.schema.id,
                schema: {
                    meta: entity.schema.meta,
                    name: entity.schema.name,
                    color: entity.schema.color,
                    enabled: entity.schema.enabled,
                    description: entity.schema.description,
                },
                attributeIdsToDelete: originalEntity?.schema.attributes.filter(attr => !entity.schema.attributes.find(a => a.id === attr.id)).map(a => a.id) || [],
                schemaDefs: entity.schema.definitions.map(def => ({
                    meta: {},
                    id: def.id,
                    enabled: true,
                    type: def.type,
                    name: def.name,
                    validation_api_check: false,
                    description: def.description,
                    attribute_schema_id: entity.schema.id,
                    validation_min: def.validationMin,
                    validation_max: def.validationMax,
                    validation_pattern: def.validationPattern,
                    validation_required: def.validationRequired,
                })),
                attributes: entity.schema.attributes.map<ArgoAttributeInsertInput>(attr => {
                    const ret: ArgoAttributeInsertInput = {
                        id: attr.id,
                        meta: attr.meta,
                        name: attr.name,
                        enabled: attr.enabled,
                        description: attr.description,
                        attribute_schema_id: entity.schema.id,
                        root_attribute_id: attr.root_attribute_id,
                        attribute_data: {
                            data: attr.attribute_data.map(ad => ({
                                id: ad.id,
                                str_val: ad.str_val,
                                num_val: ad.num_val,
                                attribute_schema_definition_id: ad.attribute_schema_definition_id,
                            })),
                            on_conflict: {
                                constraint: ArgoAttributeDataConstraint.AttributeDataPkey,
                                update_columns: [
                                    ArgoAttributeDataUpdateColumn.Enabled,
                                    ArgoAttributeDataUpdateColumn.NumVal,
                                    ArgoAttributeDataUpdateColumn.StrVal,
                                ],
                            },
                        },
                    };
                    return ret;
                }),
            },
        });

        if (entity.entitiesToUpdateOnSave.length) {
            for (const entityToUpdate of entity.entitiesToUpdateOnSave) {
                const updateEntitiesResult = await updateEntities(entityToUpdate, entity.schema.attributes);
                if (updateEntitiesResult.status !== 'success') {
                    return updateEntitiesResult;
                }
            }
        }

        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Attribute: "${JSON.stringify(err)}"`,
        };
    }
};
